import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useState } from "react";
import { Button } from "../ui/button";
import { RotateCcw, Trash2 } from "lucide-react";

function daysUntilPurge(archivedAt: number | undefined): string {
  if (!archivedAt) return "< 1 day";
  const elapsed = Date.now() - archivedAt;
  const remaining = 30 * 24 * 60 * 60 * 1000 - elapsed;
  if (remaining <= 0) return "eligible for purge";
  const days = Math.ceil(remaining / (24 * 60 * 60 * 1000));
  return `${days}d remaining`;
}

export function ArchivePanel() {
  const archived = useQuery(api.moderation.getArchivedEvents);
  const restore = useMutation(api.moderation.restoreEvent);
  const purge = useMutation(api.moderation.purgeArchivedEvents);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);

  const handlePurge = async () => {
    if (!confirm("Permanently delete all events archived more than 30 days ago? This cannot be undone.")) return;
    setPurging(true);
    setPurgeResult(null);
    try {
      const result = await purge({});
      setPurgeResult(`Deleted ${result.deleted} event${result.deleted !== 1 ? "s" : ""}.`);
    } catch (e) {
      setPurgeResult(`Error: ${String(e)}`);
    } finally {
      setPurging(false);
    }
  };

  if (!archived) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-foreground">Archived Events</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Events are permanently deleted after 30 days. Restore to return to approved status.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePurge}
          disabled={purging}
          className="text-destructive border-destructive/30 hover:border-destructive flex-shrink-0"
        >
          <Trash2 className="w-4 h-4 mr-1.5" />
          {purging ? "Purging..." : "Purge Expired"}
        </Button>
      </div>

      {purgeResult && (
        <p className="text-sm text-muted-foreground">{purgeResult}</p>
      )}

      {archived.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No archived events.</p>
      ) : (
        <div className="space-y-2">
          {archived.map((event: { _id: string; title: string; date: string; venueName: string; archivedAt?: number }) => (
            <div
              key={event._id}
              className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3"
            >
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">{event.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {event.date} · {event.venueName} · {daysUntilPurge(event.archivedAt)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => restore({ eventId: event._id as Id<"events"> })}
                className="flex-shrink-0"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Restore
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
